<template>
  <div>
    <button
      type="button"
      class="btn btn-success btn-with-addon"
      @click="showModal"
    >
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-download" />
      </span>
      Export Excel
    </button>

    <a-modal
      v-model="visible"
      title="Export Students to Excel"
      :width="820"
      style="top: 20px;"
      @cancel="handleCancel"
    >
      <template slot="footer">
        <div class="d-flex justify-content-between align-items-center">
          <span class="text-muted small">
            <i class="fe fe-info mr-1" />
            Selected Columns: <strong>{{ selectedColumns.length }}</strong>
          </span>
          <div>
            <a-button key="back" @click="handleCancel">Cancel</a-button>
            <a-button
              key="submit"
              type="primary"
              :loading="loading"
              :disabled="selectedColumns.length === 0"
              @click="handleExport"
            >
              <i class="fe fe-download mr-1" v-if="!loading" />
              Download Excel
            </a-button>
          </div>
        </div>
      </template>

      <div>
        <!-- Filter Header -->
        <div class="card bg-light border-0 mb-3">
          <div class="card-body p-3">
            <h6 class="mb-2 text-dark font-weight-bold">
              <i class="fe fe-filter mr-1" /> Export Filters
            </h6>
            <div class="row">
              <!-- Session Select -->
              <div class="col-md-3 col-6 mb-2">
                <label class="small font-weight-bold mb-1">Session</label>
                <a-select
                  v-model="filterSession"
                  style="width: 100%"
                  size="default"
                  @change="handleSessionChange"
                >
                  <a-select-option
                    v-for="s in schoolSessions"
                    :key="s.id"
                    :value="s.id"
                  >
                    {{ s.name }}
                    <small class="text-primary ml-2" v-if="s.is_current || s.is_current === 1">Current</small>
                  </a-select-option>
                </a-select>
              </div>

              <!-- Class Select -->
              <div class="col-md-3 col-6 mb-2">
                <label class="small font-weight-bold mb-1">Class</label>
                <a-select
                  v-model="filterClass"
                  placeholder="All Classes"
                  style="width: 100%"
                  size="default"
                  allowClear
                  @change="handleClassChange"
                >
                  <a-select-option :value="undefined">All Classes</a-select-option>
                  <a-select-option
                    v-for="c in schoolClasses"
                    :key="c.id"
                    :value="c.id"
                  >
                    {{ c.name }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Section Select -->
              <div class="col-md-3 col-6 mb-2">
                <label class="small font-weight-bold mb-1">Section</label>
                <a-select
                  v-model="filterSection"
                  placeholder="All Sections"
                  style="width: 100%"
                  size="default"
                  allowClear
                  :disabled="!filterClass"
                >
                  <a-select-option :value="undefined">All Sections</a-select-option>
                  <a-select-option
                    v-for="sec in sections"
                    :key="sec.id"
                    :value="sec.id"
                  >
                    {{ sec.name }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Status Select -->
              <div class="col-md-3 col-6 mb-2">
                <label class="small font-weight-bold mb-1">Student Status</label>
                <a-select
                  v-model="filterStatus"
                  placeholder="All Statuses"
                  style="width: 100%"
                  size="default"
                  allowClear
                >
                  <a-select-option :value="undefined">All Statuses</a-select-option>
                  <a-select-option :value="1">Active</a-select-option>
                  <a-select-option :value="0">Inactive</a-select-option>
                </a-select>
              </div>
            </div>
          </div>
        </div>

        <!-- Column Customizer Section -->
        <div>
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <h6 class="mb-0 text-dark font-weight-bold">
              <i class="fe fe-list mr-1" /> Choose Columns to Include
            </h6>
            <div class="btn-group btn-group-sm">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="selectBasicColumns"
              >
                Select Basic
              </button>
              <button
                type="button"
                class="btn btn-outline-primary"
                @click="selectAllColumns"
              >
                Select All
              </button>
              <button
                type="button"
                class="btn btn-outline-danger"
                @click="deselectAllColumns"
              >
                Deselect All
              </button>
            </div>
          </div>

          <!-- Quick Column Search -->
          <div class="mb-3">
            <a-input-search
              v-model="columnSearch"
              placeholder="Quick search columns..."
              size="small"
              allowClear
            />
          </div>

          <!-- Column Groups -->
          <div class="column-groups-wrapper" style="max-height: 380px; overflow-y: auto;">
            <div
              v-for="group in filteredColumnGroups"
              :key="group.title"
              class="mb-3"
            >
              <div class="d-flex align-items-center mb-2 bg-light p-2 rounded">
                <a-checkbox
                  :indeterminate="isGroupIndeterminate(group)"
                  :checked="isGroupAllChecked(group)"
                  @change="e => toggleGroupChecked(group, e.target.checked)"
                >
                  <strong class="text-secondary">{{ group.title }}</strong>
                </a-checkbox>
              </div>

              <div class="row px-2">
                <div
                  v-for="col in group.columns"
                  :key="col.key"
                  class="col-md-4 col-sm-6 mb-1"
                >
                  <a-checkbox
                    :value="col.key"
                    :checked="selectedColumns.includes(col.key)"
                    @change="e => toggleColumn(col.key, e.target.checked)"
                  >
                    <span>{{ col.label }}</span>
                  </a-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { find } from 'lodash'

export default {
  name: 'ExportStudentModal',
  props: {
    initialSession: {
      type: [Number, String],
      default: null,
    },
    initialClass: {
      type: [Number, String],
      default: undefined,
    },
    initialSection: {
      type: [Number, String],
      default: undefined,
    },
    initialSearch: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
      filterSession: this.initialSession,
      filterClass: this.initialClass,
      filterSection: this.initialSection,
      filterStatus: undefined,
      schoolSessions: [],
      schoolClasses: [],
      sections: [],
      columnSearch: '',
      defaultBasicColumns: [
        'student_code',
        'roll_no',
        'first_name',
        'last_name',
        'class',
        'section',
        'gender',
        'phone',
        'father_name',
        'status',
      ],
      selectedColumns: [
        'student_code',
        'roll_no',
        'first_name',
        'last_name',
        'class',
        'section',
        'gender',
        'phone',
        'father_name',
        'status',
      ],
      columnGroups: [
        {
          title: '1. Basic & Identification',
          columns: [
            { key: 'student_code', label: 'Student Code / Admission No' },
            { key: 'roll_no', label: 'Roll No' },
            { key: 'first_name', label: 'First Name' },
            { key: 'last_name', label: 'Last Name' },
            { key: 'full_name', label: 'Full Name' },
            { key: 'class', label: 'Class' },
            { key: 'section', label: 'Section' },
            { key: 'gender', label: 'Gender' },
            { key: 'dob', label: 'Date of Birth' },
            { key: 'admission_date', label: 'Admission Date' },
            { key: 'status', label: 'Status' },
          ],
        },
        {
          title: '2. Personal Details',
          columns: [
            { key: 'category', label: 'Category' },
            { key: 'blood_group', label: 'Blood Group' },
            { key: 'religion', label: 'Religion' },
            { key: 'caste', label: 'Caste' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'aadhaar_no', label: 'Aadhaar No' },
            { key: 'pen_no', label: 'PEN No' },
            { key: 'apaar_id', label: 'APAAR ID' },
            { key: 'house', label: 'House' },
          ],
        },
        {
          title: '3. Contact & Address',
          columns: [
            { key: 'phone', label: 'Student Mobile' },
            { key: 'email', label: 'Student Email' },
            { key: 'address', label: 'Address' },
            { key: 'city', label: 'City' },
            { key: 'district', label: 'District' },
            { key: 'state', label: 'State' },
            { key: 'pincode', label: 'Pincode' },
          ],
        },
        {
          title: '4. Parent & Guardian Details',
          columns: [
            { key: 'father_name', label: 'Father Name' },
            { key: 'father_phone', label: 'Father Phone' },
            { key: 'father_email', label: 'Father Email' },
            { key: 'father_occupation', label: 'Father Occupation' },
            { key: 'mother_name', label: 'Mother Name' },
            { key: 'mother_phone', label: 'Mother Phone' },
            { key: 'mother_email', label: 'Mother Email' },
            { key: 'guardian_name', label: 'Guardian Name' },
            { key: 'guardian_phone', label: 'Guardian Phone' },
          ],
        },
        {
          title: '5. Enrollment & System Status',
          columns: [
            { key: 'transport', label: 'Transport Enrolled' },
            { key: 'hostel', label: 'Hostel Enrolled' },
            { key: 'app_enrolled', label: 'App Registered' },
            { key: 'exit_type', label: 'Exit Type' },
            { key: 'exit_reason', label: 'Exit Reason' },
          ],
        },
      ],
    }
  },
  computed: {
    allColumnKeys() {
      const keys = []
      this.columnGroups.forEach(g => {
        g.columns.forEach(c => keys.push(c.key))
      })
      return keys
    },
    filteredColumnGroups() {
      if (!this.columnSearch) return this.columnGroups
      const query = this.columnSearch.toLowerCase()
      return this.columnGroups
        .map(group => {
          const matchedCols = group.columns.filter(c =>
            c.label.toLowerCase().includes(query) || c.key.toLowerCase().includes(query)
          )
          return {
            ...group,
            columns: matchedCols,
          }
        })
        .filter(group => group.columns.length > 0)
    },
  },
  watch: {
    initialSession(val) {
      this.filterSession = val
    },
    initialClass(val) {
      this.filterClass = val
      if (val) {
        this.getSections(val)
      } else {
        this.sections = []
      }
    },
    initialSection(val) {
      this.filterSection = val
    },
  },
  mounted() {
    this.getSessions()
    this.getClasses()
  },
  methods: {
    showModal() {
      this.visible = true
      this.filterSession = this.initialSession
      this.filterClass = this.initialClass
      this.filterSection = this.initialSection
      this.filterStatus = undefined
      if (!this.filterSession && this.schoolSessions.length) {
        const cur = find(this.schoolSessions, ['is_current', 1]) || find(this.schoolSessions, ['is_current', true])
        if (cur) this.filterSession = cur.id
      }
      this.getClasses()
      if (this.filterClass) {
        this.getSections(this.filterClass)
      }
    },
    handleCancel() {
      this.visible = false
    },
    getSessions() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        if (!this.filterSession) {
          const cur = find(data.data, ['is_current', 1]) || find(data.data, ['is_current', true])
          if (cur) this.filterSession = cur.id
        }
        this.getClasses()
      })
    },
    handleSessionChange(sessionId) {
      this.filterSession = sessionId
      this.filterClass = undefined
      this.filterSection = undefined
      this.sections = []
      this.getClasses()
    },
    getClasses() {
      this.$http
        .get('/api/class', {
          params: {
            session_id: this.filterSession,
          },
        })
        .then(({ data }) => {
          this.schoolClasses = data.data
        })
    },
    handleClassChange(classId) {
      this.filterSection = undefined
      if (classId) {
        this.getSections(classId)
      } else {
        this.sections = []
      }
    },
    getSections(classId) {
      if (!classId) return
      this.$http
        .get(`/api/section/class/${classId}`, {
          params: {
            session_id: this.filterSession,
          },
        })
        .then(({ data }) => {
          this.sections = data.data
        })
    },
    toggleColumn(key, checked) {
      if (checked) {
        if (!this.selectedColumns.includes(key)) {
          this.selectedColumns.push(key)
        }
      } else {
        this.selectedColumns = this.selectedColumns.filter(k => k !== key)
      }
    },
    selectBasicColumns() {
      this.selectedColumns = [...this.defaultBasicColumns]
    },
    selectAllColumns() {
      this.selectedColumns = [...this.allColumnKeys]
    },
    deselectAllColumns() {
      this.selectedColumns = []
    },
    isGroupAllChecked(group) {
      return group.columns.every(c => this.selectedColumns.includes(c.key))
    },
    isGroupIndeterminate(group) {
      const checkedCount = group.columns.filter(c => this.selectedColumns.includes(c.key)).length
      return checkedCount > 0 && checkedCount < group.columns.length
    },
    toggleGroupChecked(group, checked) {
      group.columns.forEach(c => {
        this.toggleColumn(c.key, checked)
      })
    },
    handleExport() {
      if (this.selectedColumns.length === 0) {
        this.$message.warning('Please select at least one column to export!')
        return
      }

      this.loading = true

      const postData = {
        session_id: this.filterSession,
        class_id: this.filterClass,
        section_id: this.filterSection,
        status: this.filterStatus,
        search: this.initialSearch,
        columns: this.selectedColumns,
      }

      const headers = {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        responseType: 'blob',
      }

      this.$http
        .post('/api/student/export', postData, headers)
        .then(response => {
          this.loading = false
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url

          const timestamp = new Date().toISOString().slice(0, 10)
          link.setAttribute('download', `Students_Export_${timestamp}.xlsx`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.visible = false
          this.$message.success('Student Excel file exported successfully!')
        })
        .catch(error => {
          this.loading = false
          console.error(error)
          if (this.$neoNotify) {
            this.$neoNotify.error(error)
          } else {
            this.$message.error('Failed to export students Excel!')
          }
        })
    },
  },
}
</script>

<style scoped>
.column-groups-wrapper::-webkit-scrollbar {
  width: 6px;
}
.column-groups-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}
</style>
